/**
 * Version of the slugification algorithm below.
 *
 * The slugs it produces are the keys that sync content across a published site AND the values that
 * appear in the public `?select=` URL parameter. Once those URLs are in the wild the algorithm is
 * frozen: changing it would silently re-resolve links people have already shared. Any future change
 * must bump this version and be gated behind it, never applied in place.
 *
 * DUPLICATED, ON PURPOSE: the renderer owns the same function at
 * `packages/gitbook/src/lib/select/slug.ts` in the GitBook Open repo. The two copies must stay
 * byte-identical in behaviour, which is why the test table is duplicated alongside it — a change on
 * either side that isn't mirrored here shows up as a failing test rather than as content that
 * silently stops syncing between the editor preview and the published site.
 */
export const SLUG_ALGO_VERSION = 1;

/**
 * Maximum slug length, counted in code points (not UTF-16 units, so we never cleave a surrogate
 * pair). Guards against pathological titles — 30 CJK characters is already ~270 bytes once
 * percent-encoded into `?select=`. Part of the frozen contract (see {@link SLUG_ALGO_VERSION}).
 */
export const SLUG_MAX_CODE_POINTS = 64;

/**
 * Turn an author-typed name (a tab title, button label, picker option…) into a `select` slug.
 *
 * DO NOT CHANGE — this is the frozen public `?select=` URL contract (see {@link SLUG_ALGO_VERSION}).
 * The output must be byte-identical on the server (baking slugs into markup/CSS) and the client
 * (parsing URLs/storage), so it relies only on locale-independent primitives: Unicode NFKC
 * normalization + `String.prototype.toLowerCase` (Unicode default case folding, not locale-sensitive).
 *
 * It keeps letters, numbers and marks from every script (so `café`, `安装`, `日本語` survive) plus a
 * small safelist of symbols — `+ # . _` — that distinguish technical names that would otherwise
 * collide (`c` vs `c++` vs `c#`, `node.js`, `on_prem`). Every other run of characters collapses to a
 * single `-`, and leading/trailing `-` are trimmed. A slug can never contain the `,` that delimits
 * `?select=`, and none of these characters need escaping in a URL-encoded query param.
 *
 * Control, format, bidi and lone-surrogate characters (`\p{C}`) are dropped outright rather than
 * turned into a `-`, and the string is re-normalized after `toLowerCase` (case mapping can leave it
 * un-normalized), so the same visible name always yields the same bytes everywhere. The result is
 * capped to {@link SLUG_MAX_CODE_POINTS} code points. Names that reduce to nothing (e.g. an
 * emoji-only title) return `''`; callers treat an empty slug as "no slug" and fall back to default.
 */
export function slugifySelectValue(name: string): string {
    const slug = name
        .normalize('NFKC')
        .replace(/\p{C}+/gu, '')
        .toLowerCase()
        .normalize('NFKC')
        .replace(/[^\p{L}\p{N}\p{M}+#._]+/gu, '-')
        .replace(/^-+|-+$/gu, '');
    // Slice by code point so a surrogate pair (e.g. astral CJK) is never cut in half, then re-trim a
    // trailing `-` the cut may have exposed.
    return [...slug].slice(0, SLUG_MAX_CODE_POINTS).join('').replace(/-+$/u, '');
}

/**
 * Resolve the `select` slug for one option — a tab, a variant, a picker entry.
 *
 * An explicit slug wins but is still slugified: it is interpolated into generated CSS attribute
 * selectors and compared against stored state, so it has to live in the same charset as a derived
 * one. Falling back to the title is what lets same-named options across a site select together with
 * no authoring, so an absent slug is the normal case, not a missing value. Returns `''` when neither
 * yields anything addressable, which callers read as "not selectable".
 */
export function resolveSelectSlug(option: { slug?: string; title?: string }): string {
    return (
        (option.slug ? slugifySelectValue(option.slug) : '') ||
        (option.title ? slugifySelectValue(option.title) : '')
    );
}
